import { AuthApi, UserRole } from '@ata-digital/client-tax-system-api-client';

interface JwtPayload {
  exp: number;
  [key: string]: unknown;
}
import { createApiConfig, apiAxiosInstance } from '../api/config';
import { handleApiError, isAuthError } from '../api/errorHandler';
import { createLogger } from '@/utils/logger';

const log = createLogger('tokenManager');

// Simple storage interface (replaces deleted secureStorage.ts)
interface SimpleStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string, expiresAt?: number): void;
  removeItem(key: string): void;
}

class LocalStorageAdapter implements SimpleStorage {
  getItem(key: string): string | null {
    return localStorage.getItem(key);
  }

  setItem(key: string, value: string, _expiresAt?: number): void {
    localStorage.setItem(key, value);
  }

  removeItem(key: string): void {
    localStorage.removeItem(key);
  }
}

/**
 * SessionStorage adapter for client authentication.
 *
 * Client auth uses sessionStorage to isolate client sessions per browser tab.
 * This prevents conflicts when a preparer (using localStorage) opens a client
 * portal in another tab - each tab gets its own isolated client session.
 *
 * Trade-off: Client sessions don't persist across tab closes, but this is
 * acceptable since client sessions are typically short-lived.
 */
class SessionStorageAdapter implements SimpleStorage {
  getItem(key: string): string | null {
    return sessionStorage.getItem(key);
  }

  setItem(key: string, value: string, _expiresAt?: number): void {
    sessionStorage.setItem(key, value);
  }

  removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }
}

export interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiresAt?: number;
}

export interface StoredTokens {
  authToken: string | null;
  refreshToken: string | null;
  userType: 'staff' | 'client' | null;
  authMethod: 'password' | 'sso' | null;
}

export interface UserInfo {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  phoneNumber?: string;
  lastLoginAt?: string;
  role?: UserRole;
}

class TokenManager {
  private refreshPromise: Promise<string> | null = null;
  // Reduced from 5 minutes to 3 minutes - tokens refresh earlier, giving more buffer time
  private readonly TOKEN_REFRESH_THRESHOLD = 3 * 60 * 1000; // 3 minutes before expiry triggers refresh
  private readonly storage: SimpleStorage;
  private sessionTimeoutId: NodeJS.Timeout | null = null;
  private readonly SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour

  constructor(storage?: SimpleStorage) {
    this.storage = storage || new LocalStorageAdapter();
    // Only start session timeout if user is already authenticated (e.g., page refresh with stored tokens)
    if (this.isAuthenticated()) {
      this.setupSessionTimeout();
    }
  }

  /**
   * Gets tokens from secure storage
   */
  private getStoredTokens(): StoredTokens {
    return {
      authToken: this.storage.getItem('authToken'),
      refreshToken: this.storage.getItem('refreshToken'),
      userType: this.storage.getItem('userType') as 'staff' | 'client' | null,
      authMethod: this.storage.getItem('authMethod') as 'password' | 'sso' | null,
    };
  }

  /**
   * Stores tokens in secure storage with expiration
   */
  private storeTokens(tokens: {
    authToken: string;
    refreshToken: string;
    userType?: 'staff' | 'client';
    authMethod?: 'password' | 'sso';
  }): void {
    const expiresAt = Date.now() + this.SESSION_TIMEOUT;

    this.storage.setItem('authToken', tokens.authToken, expiresAt);
    this.storage.setItem('refreshToken', tokens.refreshToken, expiresAt + 24 * 60 * 60 * 1000); // Refresh token valid for 24h more

    if (tokens.userType) {
      this.storage.setItem('userType', tokens.userType, expiresAt);
    }

    if (tokens.authMethod) {
      this.storage.setItem('authMethod', tokens.authMethod, expiresAt);
    }

    // Reset session timeout
    this.setupSessionTimeout();
  }

  /**
   * Clears all stored tokens and user information
   */
  public clearTokens(): void {
    this.storage.removeItem('authToken');
    this.storage.removeItem('refreshToken');
    this.storage.removeItem('userType');
    this.storage.removeItem('authMethod');
    this.storage.removeItem('userInfo');
    this.refreshPromise = null;
    this.clearSessionTimeout();
  }

  /**
   * Setup session timeout handling
   */
  private setupSessionTimeout(): void {
    this.clearSessionTimeout();

    this.sessionTimeoutId = setTimeout(() => {
      // Only trigger timeout if this manager actually has tokens stored
      // This prevents inactive managers (e.g., clientTokenManager when on preparer routes)
      // from incorrectly triggering a logout
      if (this.isAuthenticated()) {
        log.warn('Session timed out due to inactivity');
        this.clearTokens();
        // Trigger a global event for components to react to session timeout
        window.dispatchEvent(new CustomEvent('auth:session-timeout'));
      }
    }, this.SESSION_TIMEOUT);
  }

  /**
   * Clear session timeout
   */
  private clearSessionTimeout(): void {
    if (this.sessionTimeoutId) {
      clearTimeout(this.sessionTimeoutId);
      this.sessionTimeoutId = null;
    }
  }

  /**
   * Extend session timeout (call on user activity)
   */
  public extendSession(): void {
    if (this.isAuthenticated()) {
      this.setupSessionTimeout();
    }
  }

  /**
   * Validates JWT token structure and returns payload if valid
   */
  private parseJwtToken(token: string): JwtPayload | null {
    if (!token) return null;

    try {
      // JWT should have 3 parts separated by dots
      const parts = token.split('.');
      if (parts.length !== 3) {
        log.warn('Invalid JWT format: token does not have 3 parts');
        return null;
      }

      // Decode the payload (second part)
      const payload = JSON.parse(atob(parts[1]));

      // Validate required JWT claims
      if (!payload.exp || typeof payload.exp !== 'number') {
        log.warn('Invalid JWT: missing or invalid exp claim');
        return null;
      }

      return payload;
    } catch (error) {
      log.warn('Error parsing JWT token:', error);
      return null;
    }
  }

  /**
   * Checks if a JWT token is expired or will expire soon
   */
  private isTokenExpired(token: string): boolean {
    const payload = this.parseJwtToken(token);
    if (!payload) return true;

    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();

    // Consider token expired if it expires within the threshold
    return expiryTime - now < this.TOKEN_REFRESH_THRESHOLD;
  }

  /**
   * Validates if a JWT token is properly formatted and not expired.
   * Uses the same threshold as isTokenExpired for consistency.
   */
  public validateToken(token: string): boolean {
    const payload = this.parseJwtToken(token);
    if (!payload) return false;

    const expiryTime = payload.exp * 1000;
    const now = Date.now();

    // Token is valid if it won't expire within the refresh threshold
    // This ensures validateToken and isTokenExpired are consistent
    return expiryTime - now >= this.TOKEN_REFRESH_THRESHOLD;
  }

  /**
   * Gets a valid token, refreshing if necessary
   */
  public async getValidToken(): Promise<string | null> {
    const { authToken } = this.getStoredTokens();

    if (!authToken) {
      return null;
    }

    if (this.isTokenExpired(authToken)) {
      try {
        const newToken = await this.refreshToken();
        return newToken;
      } catch (error) {
        log.warn('Token refresh failed:', error);
        this.clearTokens();
        return null;
      }
    }

    return authToken;
  }

  /**
   * Refreshes the access token using the refresh token
   */
  public async refreshToken(): Promise<string> {
    // Prevent concurrent refresh calls
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performRefresh();

    try {
      const newToken = await this.refreshPromise;
      this.refreshPromise = null;
      return newToken;
    } catch (error) {
      this.refreshPromise = null;
      throw error;
    }
  }

  /**
   * Performs the actual token refresh API call with retry for transient errors.
   * Auth errors (401) are never retried — those indicate the refresh token is invalid.
   */
  private async performRefresh(): Promise<string> {
    const { refreshToken, userType } = this.getStoredTokens();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const MAX_RETRIES = 3;
    let lastError: unknown;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const authApi = new AuthApi(createApiConfig(), undefined, apiAxiosInstance);
        const response = await authApi.apiAuthRefreshTokenPost({
          refreshTokenDto: { refreshToken },
        });

        // API client returns LoginResponseDtoApiResponse with structure: { success?, data?, error?, meta? }
        // The actual LoginResponseDto is nested in response.data.data
        const loginResponse = response.data?.data;

        if (!loginResponse) {
          throw new Error('Invalid refresh response: no data');
        }

        const { token, refreshToken: newRefreshToken } = loginResponse;

        if (!token || !newRefreshToken) {
          throw new Error('Invalid refresh response');
        }

        // Store the new tokens
        this.storeTokens({
          authToken: token,
          refreshToken: newRefreshToken,
          userType: userType || undefined,
        });

        // Store user info if present in refresh response
        if (loginResponse.user) {
          this.storeUserInfo({
            id: loginResponse.user.id,
            email: loginResponse.user.email || undefined,
            firstName: loginResponse.user.firstName || undefined,
            lastName: loginResponse.user.lastName || undefined,
            fullName: loginResponse.user.fullName || undefined,
            role: loginResponse.user.role,
          });
        }

        // Dispatch event to notify listeners that token was refreshed
        log.debug('Dispatching auth:token-refreshed event');
        window.dispatchEvent(new CustomEvent('auth:token-refreshed', { detail: { token } }));

        return token;
      } catch (error) {
        lastError = error;

        // Auth errors (401) mean the refresh token itself is invalid — never retry
        if (isAuthError(error)) {
          log.warn('Refresh token rejected (401) - logging out user');
          this.clearTokens();
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
          const apiError = handleApiError(error);
          throw new Error(`Token refresh failed: ${apiError.message}`);
        }

        // For transient errors (network, 500, etc.), retry with backoff
        if (attempt < MAX_RETRIES) {
          const delayMs = 1000 * Math.pow(2, attempt); // 1s, 2s, 4s
          log.warn(`Refresh attempt ${attempt + 1} failed, retrying in ${delayMs}ms:`, error);
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }
    }

    // All retries exhausted
    log.error('Token refresh failed after all retries:', lastError);
    const apiError = handleApiError(lastError);
    throw new Error(`Token refresh failed: ${apiError.message}`);
  }

  /**
   * Checks if user is authenticated (has valid tokens)
   */
  public isAuthenticated(): boolean {
    const { authToken } = this.getStoredTokens();
    return !!authToken;
  }

  /**
   * Gets the current user type
   */
  public getUserType(): 'staff' | 'client' | null {
    const { userType } = this.getStoredTokens();
    return userType;
  }

  /**
   * Gets the stored access token (without validation)
   */
  public getStoredToken(): string | null {
    const { authToken } = this.getStoredTokens();
    return authToken;
  }

  /**
   * Gets the stored refresh token
   */
  public getStoredRefreshToken(): string | null {
    const { refreshToken } = this.getStoredTokens();
    return refreshToken;
  }

  /**
   * Manually sets tokens (used after successful login)
   */
  public setTokens(tokens: {
    authToken: string;
    refreshToken: string;
    userType: 'staff' | 'client';
    authMethod?: 'password' | 'sso';
    user?: UserInfo;
  }): void {
    this.storeTokens({
      authToken: tokens.authToken,
      refreshToken: tokens.refreshToken,
      userType: tokens.userType,
      authMethod: tokens.authMethod || 'password',
    });
    if (tokens.user) {
      this.storeUserInfo(tokens.user);
    }
  }

  /**
   * Gets the authentication method used for the current session
   */
  public getAuthMethod(): 'password' | 'sso' | null {
    const { authMethod } = this.getStoredTokens();
    return authMethod;
  }

  /**
   * Stores user information
   */
  private storeUserInfo(userInfo: UserInfo): void {
    const expiresAt = Date.now() + this.SESSION_TIMEOUT;
    this.storage.setItem('userInfo', JSON.stringify(userInfo), expiresAt);
  }

  /**
   * Gets stored user information
   */
  public getStoredUser(): UserInfo | null {
    const userInfoString = this.storage.getItem('userInfo');
    if (!userInfoString) return null;

    try {
      return JSON.parse(userInfoString);
    } catch (error) {
      log.warn('Error parsing stored user info:', error);
      return null;
    }
  }
}

/**
 * Preparer token manager - uses localStorage for persistent sessions.
 *
 * Preparers need sessions that persist across browser tabs and survive
 * tab/browser closes. localStorage provides this persistence.
 */
export const tokenManager = new TokenManager();

/**
 * Client token manager - uses sessionStorage for tab-isolated sessions.
 *
 * Client authentication is isolated per browser tab using sessionStorage.
 * This prevents conflicts when:
 * - A preparer is logged in and opens a client portal in another tab
 * - Multiple client sessions are open in different tabs
 *
 * Key differences from preparer auth:
 * - Sessions are isolated per tab (no cross-tab interference)
 * - Sessions don't persist when tab is closed
 * - Each tab can have a different client logged in
 *
 * @see SessionStorageAdapter for implementation details
 */
export const clientTokenManager = new TokenManager(new SessionStorageAdapter());

/**
 * Helper to determine if the current route is a client context.
 * Used by API interceptors and auth hooks to select the correct token manager.
 */
export function isClientContext(): boolean {
  if (typeof window === 'undefined') return false;

  const path = window.location.pathname;

  // SSO routes are only for preparer/staff users and should use preparer auth (localStorage)
  if (path.startsWith('/auth/sso')) {
    return false;
  }

  // Client context is any route that is not under the preparer namespace
  return !path.startsWith('/preparer');
}

/**
 * Returns the appropriate token manager based on the current route context.
 * - Client routes (/, /documents, /questionnaires, etc.) → clientTokenManager
 * - Preparer routes (/preparer/*) → tokenManager
 */
export function getActiveTokenManager(): TokenManager {
  return isClientContext() ? clientTokenManager : tokenManager;
}
